import { FC } from 'react';
import { Menu, Badge } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  TeamOutlined,
  FileTextOutlined,
  ContainerOutlined,
  GoldOutlined,
  SettingOutlined,
  HomeOutlined,
  PlusOutlined,
  CreditCardOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Global } from '../../constants';
import { useTranslation } from 'react-i18next';

type Props = {
  topMenu?: Boolean;
  inlineCollapsed?: Boolean | any;
  user?: any;
  /** Documentos esperando VoBo, para el contador de Documentos (PRD §4). */
  pendientes?: number;
};

const NavMenu: FC<Props> = ({ topMenu, inlineCollapsed, user, pendientes = 0 }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const isAdmin = user?.role === 'admin';
  const isSub = user?.role === 'sub';
  // El rol legal solo revisa documentos rechazados: no crea ni administra.
  const isLegal = user?.role === 'legal';

  const item = (key: string, to: string, label: string, icon: JSX.Element) => ({
    key,
    icon: !topMenu ? icon : undefined,
    label: <NavLink to={to}>{label}</NavLink>,
  });

  const trabajo = [
    item('home', Global.APP_URLS.HOME, t('navbar.home'), <HomeOutlined />),
    {
      key: 'documents',
      icon: !topMenu ? <FileTextOutlined /> : undefined,
      label: (
        <NavLink to={Global.APP_URLS.DOCUMENTS}>
          {t('navbar.documents')}
          {pendientes > 0 && (
            <Badge
              count={pendientes}
              style={{ backgroundColor: 'var(--color-accent)', marginLeft: 8 }}
            />
          )}
        </NavLink>
      ),
    },
    ...(isLegal
      ? []
      : [
          item('create', Global.APP_URLS.DOCUMENT_WIZARD, t('navbar.create'), <PlusOutlined />),
          item('create-ai', Global.APP_URLS.CREATE_AI, t('navbar.create_ai'), <ThunderboltOutlined />),
        ]),
  ];

  const biblioteca =
    isAdmin || isSub
      ? [item('templates', Global.APP_URLS.TEMPLATES, t('navbar.templates'), <ContainerOutlined />)]
      : [];
  // "Editor de plantilla" no va como ítem suelto: se entra desde una plantilla
  // concreta, con su id en la ruta.

  const administracion = [
    ...(isAdmin || isSub
      ? [item('admins', Global.APP_URLS.ADMINS, t('navbar.team'), <TeamOutlined />)]
      : []),
    ...(isAdmin
      ? [item('branchs', Global.APP_URLS.COMPANIES, t('navbar.companies'), <GoldOutlined />)]
      : []),
    ...(isAdmin || isSub
      ? [item('subscription', Global.APP_URLS.SUBSCRIPTION, t('navbar.subscription'), <CreditCardOutlined />)]
      : []),
    ...(isSub ? [item('profile', Global.APP_URLS.PROFILE, t('navbar.profile'), <SettingOutlined />)] : []),
  ];

  // Los tres grupos del PRD §4. Un grupo sin ítems visibles no se dibuja.
  const items: MenuProps['items'] = [
    { key: 'g-work', type: 'group', label: t('navbar.group_work'), children: trabajo },
    ...(biblioteca.length
      ? [{ key: 'g-library', type: 'group' as const, label: t('navbar.group_library'), children: biblioteca }]
      : []),
    ...(administracion.length
      ? [{ key: 'g-admin', type: 'group' as const, label: t('navbar.group_admin'), children: administracion }]
      : []),
  ];

  // Ruta activa: la coincidencia más larga gana, para que /admin/documents no
  // marque también a /admin.
  const rutas: Array<[string, string]> = [
    ['home', Global.APP_URLS.HOME],
    ['documents', Global.APP_URLS.DOCUMENTS],
    ['create', Global.APP_URLS.DOCUMENT_WIZARD],
    ['create-ai', Global.APP_URLS.CREATE_AI],
    ['templates', Global.APP_URLS.TEMPLATES],
    ['admins', Global.APP_URLS.ADMINS],
    ['branchs', Global.APP_URLS.COMPANIES],
    ['subscription', Global.APP_URLS.SUBSCRIPTION],
    ['profile', Global.APP_URLS.PROFILE],
  ];
  const activa = rutas
    .filter(([, ruta]) => location.pathname.startsWith(ruta))
    .sort((a, b) => b[1].length - a[1].length)[0];

  return (
    <Menu
      mode={!topMenu || window.innerWidth <= 991 ? 'inline' : 'horizontal'}
      inlineCollapsed={inlineCollapsed}
      selectedKeys={activa ? [activa[0]] : []}
      items={items}
    />
  );
};

const mapStateToProps = (state: any) => ({
  user: state.users?.data || null,
});

export default connect(mapStateToProps)(NavMenu);
